import {
  ApplicationConfig,
  provideBrowserGlobalErrorListeners,
  provideZoneChangeDetection,
} from '@angular/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideToastr } from 'ngx-toastr';
import { provideSpinnerConfig } from 'ngx-spinner';
import { provideRouter } from '@angular/router';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import {
  createInterceptorCondition,
  INCLUDE_BEARER_TOKEN_INTERCEPTOR_CONFIG,
  IncludeBearerTokenCondition,
  includeBearerTokenInterceptor,
  provideKeycloak,
} from 'keycloak-angular';
import { provideHighlightOptions } from 'ngx-highlightjs';

import { routes } from './app.routes';
import { unauthorizedInterceptor } from './interceptors/unauthorized-interceptor';
import { runtimeConfig } from './runtime-config';

// Everything below reads the RUNTIME config (fetched before bootstrap), which
// is why this is a factory rather than a constant: a constant would be built
// when the module loads, before config.json has arrived.
export function buildAppConfig(): ApplicationConfig {
  // Attach the bearer token to tracer API calls only.
  //
  // The negative lookahead keeps /healthz and /readyz unauthenticated: they are
  // registered outside the auth-gated routes on the server, and the old frontend
  // hit a real bug where the bearer interceptor blocked its version fetch at boot
  // while Keycloak was still initialising.
  const tracerUrlPattern = new RegExp(
    `^${runtimeConfig.tracerUrl}(?!/(?:healthz|readyz)(?:[?#]|$))(?:/.*)?$`,
    'i',
  );

  const tracerCondition = createInterceptorCondition<IncludeBearerTokenCondition>({
    urlPattern: tracerUrlPattern,
    bearerPrefix: 'Bearer',
  });

  // Keycloak providers, included only when auth is on.
  //
  // Spread into the provider list rather than registered unconditionally: with
  // authEnabled=false there is no Keycloak provider at all, so nothing contacts
  // the auth server and app boot cannot hang on it being unreachable.
  const authProviders = runtimeConfig.authEnabled
    ? [
        provideKeycloak({
          config: {
            url: runtimeConfig.keycloakUrl,
            // The realm is switchable at runtime: operators on the admins realm set
            // localStorage.agRealm. Carried over from the old app, where the same
            // key drove the agents/admins split.
            realm: localStorage.getItem('agRealm') || runtimeConfig.keycloakRealm,
            clientId: runtimeConfig.keycloakClientId,
          },
          initOptions: {
            // check-sso rather than login-required: the list should render for an
            // already-authenticated operator without an interstitial redirect.
            onLoad: 'check-sso',
            silentCheckSsoRedirectUri: `${window.location.origin}/assets/silent-check-sso.html`,
          },
        }),
        {
          provide: INCLUDE_BEARER_TOKEN_INTERCEPTOR_CONFIG,
          useValue: [tracerCondition],
        },
      ]
    : [];

  return {
    providers: [
      provideBrowserGlobalErrorListeners(),
      provideZoneChangeDetection({ eventCoalescing: true }),
      provideRouter(routes),
      provideAnimations(),
      // The ported v1 components use Toastr for errors and NgxSpinner for
      // loading state; both need their providers registered or the app fails to
      // bootstrap with NG0201 at runtime — which the build does not catch.
      provideToastr({
        positionClass: 'toast-bottom-center',
        timeOut: 3000,
        closeButton: true,
        preventDuplicates: true,
      }),
      provideSpinnerConfig({ type: 'ball-clip-rotate-multiple' }),
      ...authProviders,
      // Both interceptors exist only when auth is on. With it off there is no
      // session to attach a token to or to end — a 401 then is a backend
      // misconfiguration, not something the UI should react to.
      provideHttpClient(
        withInterceptors(
          runtimeConfig.authEnabled ? [includeBearerTokenInterceptor, unauthorizedInterceptor] : [],
        ),
      ),
      provideHighlightOptions({
        coreLibraryLoader: () => import('highlight.js/lib/core'),
        languages: {
          xml: () => import('highlight.js/lib/languages/xml'),
          json: () => import('highlight.js/lib/languages/json'),
        },
      }),
    ],
  };
}
